/**
 * Quote API to compare UI normalization (Shape quote.json and submission detail quotes).
 * Shared, byte-identical, by both workbench frontends.
 */

function firstNonEmpty(...values) {
  return values.find((v) => v !== null && v !== undefined && v !== "");
}

function shapeQuotePackageFromDetail(detail) {
  const pkg = detail?.shapeQuotePackage;
  return pkg && typeof pkg === "object" ? pkg : null;
}

function hasDisplayValue(value) {
  return value != null && value !== "";
}

/** Normalize Shape ``coverages[]`` from stored ``shapeQuotePackage``. */
export function normalizeShapeCoverages(coverages) {
  if (!Array.isArray(coverages)) return [];
  return coverages
    .filter((c) => c && typeof c === "object")
    .map((c) => ({
      code: c.code || "",
      displayName: c.display_name || c.displayName || c.code || "Coverage",
      policyLimit: c.policy_limit ?? c.policyLimit,
      aggregateLimit: c.aggregate_limit ?? c.aggregateLimit,
      deductible: c.deductible,
      annualPremium: c.annual_premium ?? c.annualPremium,
      perOccurrenceLimit: c.per_occurrence_limit ?? c.perOccurrenceLimit,
      claimLimit: c.claim_limit ?? c.claimLimit,
    }));
}

export function subLimitsFromApiQuote(q) {
  let scanned = q?.scannedSublimits;
  if (typeof scanned === "string" && scanned.trim()) {
    try {
      scanned = JSON.parse(scanned);
    } catch {
      scanned = null;
    }
  }
  if (scanned && typeof scanned === "object" && !Array.isArray(scanned) && Object.keys(scanned).length) {
    return scanned;
  }
  return null;
}

const MARKET_TYPE_LABELS = { admitted: "Admitted", surplus_lines: "Surplus lines" };

/**
 * The market slot's label: UWB's ``marketType`` (``admitted`` / ``surplus_lines``)
 * as "Admitted" / "Surplus lines", or "" when it is not set.
 * Never the quote type ("full_quotes"), which is not a market.
 */
export function marketTypeLabel(marketType) {
  const key = String(marketType || "")
    .trim()
    .toLowerCase();
  return MARKET_TYPE_LABELS[key] || "";
}

/**
 * The matrix pill's label. UWB's ``marketType`` wins. A legacy MShift quote has no
 * ``marketType``, only free text in ``marketDisplay``: recognised Admitted / Surplus
 * lines wording maps to the same two labels, anything else shows nothing. Never the raw
 * text, which is how "full_quotes" reached the pill.
 */
export function marketPillLabel(marketType, marketDisplay) {
  const typed = marketTypeLabel(marketType);
  if (typed) return typed;
  const lower = String(marketDisplay || "")
    .trim()
    .toLowerCase();
  if (!lower) return "";
  if (/surplus|non[-\s]?admit|un-?admit|not\s+admit/.test(lower)) return "Surplus lines";
  if (lower.includes("admit")) return "Admitted";
  return "";
}

/**
 * Coverage breakdown for compare cards and the quote detail modal.
 * Limit fields on detail come from Shape quote.json (policyLimit, aggregateLimit, ...).
 */
export function buildCoverageDetailsFromApiQuote(q) {
  const detail = q?.detail || {};
  return {
    coverageLimit: firstNonEmpty(detail.policyLimit, q?.coverageLimitAmount),
    deductible: firstNonEmpty(detail.deductible, q?.deductibleAmount),
    claimLimit: firstNonEmpty(detail.claimLimit),
    // UWB sends ``occurrenceLimit``; ``perOccurrenceLimit``
    // is the old callback key, kept for quotes stored before that.
    eachOccurrenceLimit: firstNonEmpty(detail.occurrenceLimit, detail.perOccurrenceLimit),
    aggregateLimit: firstNonEmpty(detail.aggregateLimit, detail.policyLimit, q?.coverageLimitAmount),
  };
}

const OPTIONAL_SHAPE_COVERAGE_ROWS = [
  { field: "claimLimit", keyPrefix: "cl", label: "Claim Limit" },
  { field: "perOccurrenceLimit", keyPrefix: "eo", label: "Each occurrence limit" },
  { field: "aggregateLimit", keyPrefix: "agg", label: "Aggregate Limit" },
  { field: "annualPremium", keyPrefix: "ap", label: "Annual Premium" },
];

function buildShapeCoverageDisplayRows(shapeCoverages) {
  const showHeaders = shapeCoverages.length > 1;
  const rows = [];

  for (const [idx, cov] of shapeCoverages.entries()) {
    if (showHeaders) {
      rows.push({ key: `hdr-${idx}`, label: cov.displayName, header: true });
    }
    rows.push(
      { key: `pl-${idx}`, label: "Coverage Limit", value: cov.policyLimit },
      { key: `ded-${idx}`, label: "Deductible", value: cov.deductible }
    );
    for (const { field, keyPrefix, label } of OPTIONAL_SHAPE_COVERAGE_ROWS) {
      const value = cov[field];
      if (hasDisplayValue(value)) {
        rows.push({ key: `${keyPrefix}-${idx}`, label, value });
      }
    }
  }

  return rows;
}

/** UWB's retention terms, in the order the card and the quote window show them. */
const UWB_RETENTION_ROWS = [
  { key: "ded", label: "Per-claim retention", value: (d) => d.perClaimRetention },
  { key: "por", label: "Per-occurrence retention", value: (d) => d.perOccurrenceRetention },
  // ``perAggregateRetention`` is the old callback key, kept for quotes stored before.
  {
    key: "agr",
    label: "Aggregate retention",
    value: (d) => firstNonEmpty(d.aggregateRetention, d.perAggregateRetention),
  },
];

/**
 * Whether the quote carries at least one UWB retention figure. Keys present but all
 * null (readiness's mapper always writes them) do not count: such a quote keeps its
 * "Deductible" row.
 */
export function hasUwbRetentionFigures(q) {
  const detail = q?.detail;
  if (!detail || typeof detail !== "object") return false;
  return UWB_RETENTION_ROWS.some(({ value }) => hasDisplayValue(value(detail)));
}

/**
 * UWB's sub-limits (``detail.coverages``: ``[{name, limit, retention}]``), as raw rows for
 * the Sub Limits section: one for the limit under the name UWB sent, and one for its
 * retention. Values are left unformatted; an entry with no name is skipped.
 */
export function buildUwbSubLimitRows(q) {
  const coverages = q?.detail?.coverages;
  if (!Array.isArray(coverages)) return [];
  const rows = [];
  coverages.forEach((coverage, index) => {
    if (!coverage || typeof coverage !== "object") return;
    const name = typeof coverage.name === "string" ? coverage.name.trim() : "";
    if (!name) return;
    rows.push({ key: `uwb-${index}`, label: name, value: coverage.limit ?? null });
    rows.push({ key: `uwb-${index}-retention`, label: `${name} retention`, value: coverage.retention ?? null });
  });
  return rows;
}

/**
 * Rows for the Coverage Details UI. Uses Shape ``coverages[]`` when present;
 * otherwise the flat summary from the callback fields.
 */
export function buildCoverageDisplayRows(q) {
  const pkg = shapeQuotePackageFromDetail(q?.detail || {});
  const shapeCoverages = normalizeShapeCoverages(pkg?.coverages);

  if (shapeCoverages.length) {
    return buildShapeCoverageDisplayRows(shapeCoverages);
  }

  const cd = buildCoverageDetailsFromApiQuote(q);
  const detail = q?.detail || {};
  // UWB sends three retentions (S2CWB-414): each gets its own row, in place of the one
  // "Deductible" that showed the per-claim retention, else the per-occurrence one.
  const retentionRows = hasUwbRetentionFigures(q)
    ? UWB_RETENTION_ROWS.map(({ key, label, value }) => ({ key, label, value: value(detail) ?? null }))
    : [{ key: "ded", label: "Deductible", value: firstNonEmpty(cd.deductible, q?.retention) }];
  return [
    { key: "pl", label: "Coverage Limit", value: firstNonEmpty(cd.coverageLimit, q?.coverageLimit) },
    ...retentionRows,
    { key: "cl", label: "Claim Limit", value: cd.claimLimit },
    { key: "eo", label: "Each occurrence limit", value: cd.eachOccurrenceLimit },
    { key: "agg", label: "Aggregate Limit", value: cd.aggregateLimit },
  ];
}
